import React, { useRef } from 'react';
import { useAdoptedStyleSheet } from './useAdoptedStyleSheet';

type SkeletonSize = 'small' | 'default' | 'large';
type SkeletonShape = 'default' | 'round' | 'circle' | 'square';

interface BaseSkeletonProps {
    active?: boolean;
    className?: string;
}

interface SkeletonButtonProps extends BaseSkeletonProps {
    size?: SkeletonSize;
    shape?: SkeletonShape;
    block?: boolean;
    width?: number | string;
    height?: number | string;
    minWidth?: number | string;
}

interface SkeletonInputProps extends BaseSkeletonProps {
    size?: SkeletonSize;
    block?: boolean;
    width?: number | string;
    height?: number | string;
    minWidth?: number | string;
}

interface SkeletonAvatarProps extends BaseSkeletonProps {
    size?: SkeletonSize | number;
    shape?: 'circle' | 'square';
}

const BUTTON_HEIGHT: Record<SkeletonSize, number> = { small: 24, default: 32, large: 40 };
const BUTTON_WIDTH: Record<SkeletonSize, number> = { small: 64, default: 96, large: 128 };
const INPUT_HEIGHT: Record<SkeletonSize, number> = { small: 24, default: 32, large: 40 };
const AVATAR_SIZE: Record<SkeletonSize, number> = { small: 24, default: 32, large: 40 };

const KEYFRAME_NAME = 'malachite-skeleton-loading';
const SKELETON_BASE = 'rgba(190, 190, 190, 0.2)';
const SKELETON_SHIMMER = 'rgba(129, 129, 129, 0.24)';
const SKELETON_GRADIENT = `linear-gradient(90deg, ${SKELETON_BASE} 25%, ${SKELETON_SHIMMER} 37%, ${SKELETON_BASE} 63%)`;
const KEYFRAMES_CSS = `@keyframes ${KEYFRAME_NAME} { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }`;

const getRadius = (shape: SkeletonShape): string | number => {
    switch (shape) {
        case 'round':
        case 'circle':
            return 9999;
        case 'square':
            return 0;
        default:
            return 6;
    }
};

const baseStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    verticalAlign: 'middle',
    background: active ? SKELETON_GRADIENT : SKELETON_BASE,
    backgroundSize: active ? '400% 100%' : undefined,
    animation: active ? `${KEYFRAME_NAME} 1.4s ease infinite` : undefined,
    flexShrink: 0,
});

const SkeletonButton: React.FC<SkeletonButtonProps> = ({
    size = 'default',
    shape = 'default',
    block = false,
    active = true,
    className,
    width,
    height,
    minWidth,
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    useAdoptedStyleSheet(ref, KEYFRAMES_CSS, 'malachite-skeleton-keyframes');
    return (
        <span
            ref={ref}
            className={className}
            aria-busy="true"
            aria-hidden="true"
            style={{
                ...baseStyle(active),
                width: block ? '100%' : (width ?? BUTTON_WIDTH[size]),
                height: height ?? BUTTON_HEIGHT[size],
                minWidth,
                borderRadius: getRadius(shape),
            }}
        />
    );
};

const SkeletonInput: React.FC<SkeletonInputProps> = ({
    size = 'default',
    block = false,
    active = true,
    className,
    width,
    height,
    minWidth,
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    useAdoptedStyleSheet(ref, KEYFRAMES_CSS, 'malachite-skeleton-keyframes');
    return (
        <span
            ref={ref}
            className={className}
            aria-busy="true"
            aria-hidden="true"
            style={{
                ...baseStyle(active),
                width: block ? '100%' : (width ?? 160),
                height: height ?? INPUT_HEIGHT[size],
                minWidth,
                borderRadius: 6,
            }}
        />
    );
};

const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({
    size = 'default',
    shape = 'circle',
    active = true,
    className,
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    useAdoptedStyleSheet(ref, KEYFRAMES_CSS, 'malachite-skeleton-keyframes');
    const px = typeof size === 'number' ? size : AVATAR_SIZE[size];
    return (
        <span
            ref={ref}
            className={className}
            aria-busy="true"
            aria-hidden="true"
            style={{
                ...baseStyle(active),
                width: px,
                height: px,
                borderRadius: shape === 'circle' ? 9999 : 6,
            }}
        />
    );
};

const Skeleton = {
    Button: SkeletonButton,
    Input: SkeletonInput,
    Avatar: SkeletonAvatar,
};

export default Skeleton;
